// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch, request } from '@/lib/api'
import type { PatientDetail } from '@/types'
import { adaptPatient, prepNoteEnriched, type ApiPatientDetail, type PullStatus } from '@/pages/PatientDetailPage'

// A forced clinical pull lands async: poll GET patient detail until the pull is terminal,
// then keep refetching briefly so the event-driven records surface. The cap is a soft nudge.
const REFRESH_POLL_MS = 4_000
const REFRESH_TIMEOUT_MS = 5 * 60 * 1_000
const LIVE_DETAIL_POLL_MS = 15_000

// A completed intake persists a subjective note first, then enriches it (assessment /
// differential / plan / HIE) in a detached step landing ~40s later. Poll until the note
// (and its enrichment) surfaces; the cap ends the silent poll on the rare miss.
const NOTE_POLL_MS = 3_000
const NOTE_TIMEOUT_MS = 90 * 1_000

const TERMINAL: ReadonlySet<PullStatus> = new Set(['completed', 'failed', 'no_data', 'skipped_fresh'])
const isTerminal = (s: PullStatus) => TERMINAL.has(s)
const clinicalDataAdvanced = (cur?: string | null, baseline?: string | null) => !!cur && cur !== baseline

export type Outcome = { kind: PullStatus | 'fresh' | 'timedout'; errorCode?: string } | null

// True while any encounter has completed intake (prep_ready) but its note hasn't fully
// landed — either not generated yet (null) or generated but not yet enriched. Per-encounter
// (not patient-level) so it matches exactly what the encounter card renders, and it covers
// BOTH the initial note arrival and the later enrichment step.
function anyNoteSettling(ap: ApiPatientDetail | null): boolean {
  return (ap?.appointments ?? []).some((a) => a.intakeStatus === 'prep_ready' && !prepNoteEnriched(a.prepNote))
}

export interface PatientDetailState {
  patient: PatientDetail | null
  apiPatient: ApiPatientDetail | null
  fetchStatus: 'loading' | 'done' | 'notfound' | 'error'
  fetchError: string
  reload: () => void
  refreshRecords: () => Promise<void>
  syncing: boolean
  catchingUp: boolean
  outcome: Outcome
}

// Single source of truth for a patient-detail view: one GET /v1/patients/{id} fetch plus
// ALL the eventual-consistency catch-up polling (clinical pull + prep-note settling), so
// every screen that shows patient/encounter data auto-refreshes instead of stranding a
// loading state until a manual browser reload. Consumed by PatientDetailPage AND
// EncounterDetailPage — the polling lives here so no screen can forget it.
export function usePatientDetail(id: string | undefined): PatientDetailState {
  const [patient, setPatient] = useState<PatientDetail | null>(null)
  const [apiPatient, setApiPatient] = useState<ApiPatientDetail | null>(null)
  const [fetchStatus, setFetchStatus] = useState<'loading' | 'done' | 'notfound' | 'error'>('loading')
  const [fetchError, setFetchError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const reload = useCallback(() => setReloadKey((k) => k + 1), [])

  const [syncing, setSyncing] = useState(false)
  const [catchingUp, setCatchingUp] = useState(false)
  const [outcome, setOutcome] = useState<Outcome>(null)
  const syncBaselineRef = useRef<string | null>(null)
  const clinicalBaselineRef = useRef<string | null>(null)
  // First load shows the full-page loader; poll refetches update in the BACKGROUND —
  // no spinner takeover, no wiping a loaded view.
  const loadedOnceRef = useRef(false)

  useEffect(() => {
    if (!id) {
      setFetchStatus('notfound')
      return
    }
    const controller = new AbortController()
    if (!loadedOnceRef.current) {
      setFetchStatus('loading')
      setFetchError('')
    }
    ;(async () => {
      try {
        const res = await apiFetch(`/v1/patients/${encodeURIComponent(id)}`, { signal: controller.signal })
        if (res.status === 404) {
          if (!loadedOnceRef.current) setFetchStatus('notfound')
          return
        }
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        const data = (await res.json()) as ApiPatientDetail
        setApiPatient(data)
        setPatient(adaptPatient(data))
        loadedOnceRef.current = true
        setFetchStatus('done')
      } catch (err) {
        if (controller.signal.aborted) return
        if (!loadedOnceRef.current) {
          setFetchError(err instanceof Error ? err.message : 'Unknown error')
          setFetchStatus('error')
        }
      }
    })()
    return () => controller.abort()
  }, [id, reloadKey])

  // Keep encounter/intake status live while staff are coordinating on the same visit.
  useEffect(() => {
    if (!id) return
    const iv = setInterval(() => setReloadKey((k) => k + 1), LIVE_DETAIL_POLL_MS)
    return () => clearInterval(iv)
  }, [id])

  // Manual "Refresh records": force a re-pull (bypasses the staleness gate). The pull lands
  // async, so on "syncing" the catch-up polls take over until the pull is terminal.
  const refreshRecords = useCallback(async () => {
    if (!id || syncing) return
    syncBaselineRef.current = apiPatient?.latestPull?.startedAt ?? null
    clinicalBaselineRef.current = apiPatient?.clinicalDataUpdatedAt ?? null
    setOutcome(null)
    setCatchingUp(false)
    setSyncing(true)
    try {
      const res = await request<{ status: 'syncing' | 'fresh'; clinicalDataUpdatedAt: string | null; pullId: string | null }>(
        `/v1/patients/${encodeURIComponent(id)}/refresh-clinical`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ force: true }) },
      )
      if (res.status === 'fresh') {
        setSyncing(false)
        setOutcome({ kind: 'fresh' })
      }
    } catch {
      setSyncing(false)
      setOutcome({ kind: 'failed' })
    }
  }, [id, syncing, apiPatient])

  // Clinical-pull catch-up: while syncing OR catching up, silently refetch on an interval.
  useEffect(() => {
    if (!syncing && !catchingUp) return
    setReloadKey((k) => k + 1)
    let elapsed = 0
    const iv = setInterval(() => {
      elapsed += REFRESH_POLL_MS
      setReloadKey((k) => k + 1)
      if (elapsed >= REFRESH_TIMEOUT_MS) {
        if (syncing) setOutcome({ kind: 'timedout' })
        setSyncing(false)
        setCatchingUp(false)
      }
    }, REFRESH_POLL_MS)
    return () => clearInterval(iv)
  }, [syncing, catchingUp])

  // A NEW pull reaching terminal status. For completed, the rich records land via an async
  // event AFTER the status flips, so stop the user-facing spinner but keep polling silently
  // until that data surfaces. Any other terminal status: stop and surface the outcome.
  useEffect(() => {
    if (!syncing) return
    const lp = apiPatient?.latestPull
    if (!lp || !isTerminal(lp.status)) return
    if (lp.startedAt === syncBaselineRef.current) return
    setSyncing(false)
    if (lp.status === 'completed') {
      setCatchingUp(!clinicalDataAdvanced(apiPatient?.clinicalDataUpdatedAt, clinicalBaselineRef.current))
    } else {
      setCatchingUp(false)
      setOutcome({ kind: lp.status, errorCode: lp.errorCode })
    }
  }, [apiPatient, syncing])

  // Post-completion catch-up: stop once the event-driven clinical data has advanced past
  // the refresh-start baseline (the sections have filled in).
  useEffect(() => {
    if (!catchingUp) return
    if (clinicalDataAdvanced(apiPatient?.clinicalDataUpdatedAt, clinicalBaselineRef.current)) {
      setCatchingUp(false)
    }
  }, [apiPatient, catchingUp])

  // Prep-note catch-up: silently refetch while any completed encounter's note is still
  // settling (missing OR not yet enriched), so it fills in without a manual reload.
  // Bounded; exhausts after the window, and re-arms only when a new note starts settling.
  const notesSettling = anyNoteSettling(apiPatient)
  const noteExhaustedRef = useRef(false)
  useEffect(() => {
    if (!notesSettling) {
      noteExhaustedRef.current = false
      return
    }
    if (noteExhaustedRef.current) return
    let elapsed = 0
    const iv = setInterval(() => {
      elapsed += NOTE_POLL_MS
      setReloadKey((k) => k + 1)
      if (elapsed >= NOTE_TIMEOUT_MS) {
        noteExhaustedRef.current = true
        clearInterval(iv)
      }
    }, NOTE_POLL_MS)
    return () => clearInterval(iv)
  }, [notesSettling])

  return { patient, apiPatient, fetchStatus, fetchError, reload, refreshRecords, syncing, catchingUp, outcome }
}
